import React, { useState } from 'react';
import { Box, Button, Dialog, DialogContent, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { colors, defaultButtonSX } from '@src/styles';
import { enqueueSnackbar } from 'notistack';

interface CustomJsonViewerProps {
  data: unknown;
  title?: string;
}

const CustomJsonViewer: React.FC<CustomJsonViewerProps> = ({ data }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCopyJson = async () => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 1000 });
    } catch (error) {
      console.error('Failed to copy JSON:', error);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderJsonContent = (jsonData: unknown, level: number = 0): React.ReactNode => {
    if (jsonData === null) return <span style={{ color: colors.background.light, fontStyle: 'italic' }}>null</span>;
    if (typeof jsonData === 'undefined')
      return <span style={{ color: colors.background.light, fontStyle: 'italic' }}>undefined</span>;
    if (typeof jsonData === 'string') {
      const formattedString = jsonData.replace(/\n/g, '\n').replace(/\t/g, '\t').replace(/\r/g, '\r');

      return <span style={{ color: colors.primary.green, whiteSpace: 'pre-wrap' }}>"{formattedString}"</span>;
    }
    if (typeof jsonData === 'number') return <span style={{ color: colors.primary.blue }}>{jsonData}</span>;
    if (typeof jsonData === 'boolean') {
      if (jsonData) return <span style={{ color: colors.primary.green }}>{jsonData.toString()}</span>;
      return <span style={{ color: colors.primary.warning }}>{jsonData.toString()}</span>;
    }

    if (Array.isArray(jsonData)) {
      if (jsonData.length === 0) {
        return (
          <>
            <span style={{ color: colors.primary.white }}>[</span>
            <span style={{ color: colors.background.light, fontStyle: 'italic' }}>empty array</span>
            <span style={{ color: colors.primary.white }}>]</span>
          </>
        );
      }
      return (
        <>
          <span style={{ color: colors.primary.white }}>[</span>
          <Box sx={{ ml: 2 }}>
            {jsonData.map((item, index) => (
              <Box key={index} sx={{ display: 'block' }}>
                {renderJsonContent(item, level + 1)}
                {index < jsonData.length - 1 && <span style={{ color: colors.primary.white }}>,</span>}
              </Box>
            ))}
          </Box>
          <span style={{ color: colors.primary.white }}>]</span>
        </>
      );
    }

    if (typeof jsonData === 'object') {
      const keys = Object.keys(jsonData);
      if (keys.length === 0) {
        return (
          <>
            <span style={{ color: colors.primary.white }}>{'{'}</span>
            <span style={{ color: colors.background.light, fontStyle: 'italic' }}>empty object</span>
            <span style={{ color: colors.primary.white }}>{'}'}</span>
          </>
        );
      }
      return (
        <>
          <span style={{ color: colors.primary.white }}>{'{'}</span>
          <Box sx={{ ml: 2 }}>
            {keys.map((key, index) => (
              <Box key={key} sx={{ display: 'block' }}>
                <span style={{ color: colors.primary.yellow }}>"{key}"</span>
                <span style={{ color: colors.primary.white }}>: </span>
                {renderJsonContent(jsonData[key], level + 1)}
                {index < keys.length - 1 && <span style={{ color: colors.primary.white }}>,</span>}
              </Box>
            ))}
          </Box>
          <span style={{ color: colors.primary.white }}>{'}'}</span>
        </>
      );
    }

    return <span style={{ color: colors.primary.white }}>{String(jsonData)}</span>;
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
        <Tooltip title="Ver Detalhes" arrow>
          <Button
            size="small"
            onClick={handleOpenModal}
            sx={{
              ...defaultButtonSX,
              minWidth: 'auto',
              padding: '6px',
              width: 28,
              height: 28,
              color: colors.primary.white,
              '&:hover': {
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
              },
            }}
          >
            <VisibilityIcon sx={{ fontSize: '16px', color: 'inherit' }} />
          </Button>
        </Tooltip>
        <Tooltip title="Copiar JSON" arrow>
          <Button
            size="small"
            onClick={handleCopyJson}
            sx={{
              ...defaultButtonSX,
              minWidth: 'auto',
              padding: '6px',
              width: 28,
              height: 28,
              color: colors.primary.white,
              '&:hover': {
                backgroundColor: colors.primary.yellow,
                color: colors.background.primary,
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: '16px', color: 'inherit' }} />
          </Button>
        </Tooltip>
      </Box>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(6px)',
              backgroundColor: 'rgba(10, 13, 15, 0.55)',
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: colors.brand.surface,
            backgroundImage: 'none',
            border: `1px solid ${colors.brand.line}`,
            borderRadius: '14px',
            boxShadow: '0 30px 80px rgba(0, 0, 0, 0.45)',
          },
        }}
      >
        <DialogContent sx={{ p: 2, backgroundColor: colors.brand.surface }}>
          <Box
            sx={{
              maxHeight: '70vh',
              overflow: 'auto',
              backgroundColor: colors.background.primary,
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.5',
              p: 1.5,
              borderRadius: '6px',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {renderJsonContent(data)}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomJsonViewer;
